import { useRef, type ChangeEvent } from "react";
import { Globe, Zap, FileText, Clock, CheckCircle2 } from "lucide-react";

// Available LLM models for OJK analysis
export const ANALYZE_MODELS = [
  { value: "claude-3-5-haiku",   label: "Claude 3.5 Haiku (Fast)" },
  { value: "gemini-2.0-flash",   label: "Gemini 2.0 Flash"        },
  { value: "gpt-4o",             label: "GPT-4o"                   },
  { value: "claude-sonnet-4-6",  label: "Claude Sonnet 4.6"       },
] as const;

export type AnalyzeModelValue = (typeof ANALYZE_MODELS)[number]["value"];

export interface IngestionSectionProps {
  ojkUrl: string;
  onUrlChange: (v: string) => void;
  onFetch: () => void;
  ojkLoading: boolean;
  ojkDone: boolean;
  ojkError: string;
  ojkSummary: string;
  ojkRelevant: boolean;
  bannedCount: number;
  restrictedCount: number;
  lastFetchTime: Date | null;
  /** Currently selected LLM model for analysis */
  analyzeModel: AnalyzeModelValue;
  onModelChange: (m: AnalyzeModelValue) => void;
  onPdfUpload: (file: File) => Promise<void>;
  pdfLoading: boolean;
}

export default function IngestionSection({
  ojkUrl,
  onUrlChange,
  onFetch,
  ojkLoading,
  ojkDone,
  ojkError,
  ojkSummary,
  ojkRelevant,
  bannedCount,
  restrictedCount,
  lastFetchTime,
  analyzeModel,
  onModelChange,
  onPdfUpload,
  pdfLoading,
}: IngestionSectionProps) {
  const pdfInputRef = useRef<HTMLInputElement | null>(null);

  function formatFetchTime(d: Date): string {
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 1) return "just now";
    if (diff === 1) return "1 min ago";
    if (diff < 60) return `${diff} mins ago`;
    const h = Math.floor(diff / 60);
    return h === 1 ? "1 hour ago" : `${h} hours ago`;
  }

  function handleBrowseClick() {
    if (pdfLoading) return;
    pdfInputRef.current?.click();
  }

  async function handlePdfChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await onPdfUpload(file);
    // Allow selecting the same file again.
    e.target.value = "";
  }

  return (
    <section className="mb-8">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Compliance Ingestion</h2>
        <p className="text-sm text-gray-400 mt-1">
          Update regulatory benchmarks by crawling OJK portals or uploading policy mandates.
        </p>
      </div>

      <div className="flex gap-4 items-stretch">
        {/* ── URL Fetcher card ──────────────────────────────── */}
        <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">OJK Regulatory Portal URL</p>

          {/* Input + model selector + button row */}
          <div className="flex gap-2 items-center">
            {/* URL input */}
            <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 focus-within:border-indigo-300 focus-within:ring-1 focus-within:ring-indigo-100 transition-all min-w-0">
              <Globe size={14} className="text-gray-400 flex-shrink-0" />
              <input
                type="url"
                value={ojkUrl}
                onChange={(e) => onUrlChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onFetch(); }}
                placeholder="https://www.ojk.go.id/id/regulasi/..."
                className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none min-w-0"
              />
            </div>

            {/* Model selector — compact, fixed width so it never wraps */}
            <select
              value={analyzeModel}
              onChange={(e) => onModelChange(e.target.value as AnalyzeModelValue)}
              disabled={ojkLoading}
              title="Select analysis model"
              className="flex-shrink-0 h-[42px] bg-gray-50 border border-gray-200 rounded-xl px-3 text-xs font-medium text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-300 cursor-pointer hover:border-indigo-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed appearance-none pr-7 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_10px_center]"
            >
              {ANALYZE_MODELS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>

            {/* Fetch & Analyze button */}
            <button
              onClick={onFetch}
              disabled={ojkLoading || !ojkUrl.trim()}
              className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-indigo-200"
            >
              {ojkLoading ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap size={13} />
                  Fetch &amp; Analyze
                </>
              )}
            </button>
          </div>

          {/* Status line */}
          <div className="mt-3 flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <Clock size={11} />
              {lastFetchTime ? `Last fetch: ${formatFetchTime(lastFetchTime)}` : "Not fetched yet"}
            </span>
            {ojkDone && ojkRelevant && (
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <CheckCircle2 size={11} />
                {bannedCount + restrictedCount} new regulations mapped
              </span>
            )}
          </div>

          {/* Loading progress */}
          {ojkLoading && (
            <div className="mt-4 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 text-xs text-indigo-600 flex items-center gap-2">
              <span className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
              Reading Document → Extracting Entities → Updating Database...
            </div>
          )}

          {/* Error */}
          {ojkError && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-700">
              ❌ {ojkError}
            </div>
          )}

          {/* Result stats */}
          {ojkDone && (
            <div className="mt-4">
              {!ojkRelevant ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-500">
                  ℹ️ URL is not related to Indonesian financial services — no violations detected.
                  {ojkSummary && <p className="mt-1 text-gray-400">{ojkSummary}</p>}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl bg-red-50 border border-red-100 p-3 text-center">
                    <div className="text-2xl font-bold text-red-600">{bannedCount}</div>
                    <div className="text-[11px] text-red-400 mt-0.5 leading-tight">Illegal Platforms</div>
                  </div>
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center">
                    <div className="text-2xl font-bold text-amber-600">{restrictedCount}</div>
                    <div className="text-[11px] text-amber-400 mt-0.5 leading-tight">Restricted Phrases</div>
                  </div>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
                    <div className="text-2xl font-bold text-emerald-600">1</div>
                    <div className="text-[11px] text-emerald-400 mt-0.5 leading-tight">Mandatory Disclaimer</div>
                  </div>
                </div>
              )}
              {ojkSummary && ojkRelevant && (
                <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-3 py-2 mt-3">{ojkSummary}</p>
              )}
            </div>
          )}
        </div>

        {/* ── PDF upload card ───────────────────────────────── */}
        <div className="w-[220px] bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col items-center justify-center gap-3 text-center">
          <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-dashed border-gray-300 flex items-center justify-center">
            <FileText size={22} className="text-gray-300" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700">PDF Policy Docs</p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              Drop OJK circular letters or internal compliance updates here
            </p>
          </div>
          <button
            type="button"
            onClick={handleBrowseClick}
            disabled={pdfLoading}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pdfLoading ? "Analyzing PDF..." : "Browse files"}
          </button>
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => { void handlePdfChange(e); }}
          />
        </div>
      </div>
    </section>
  );
}
